/**
 * Date/time formatting helpers — single source of truth for deadlines.
 *
 * Every formatter renders in the user\'s preferred timezone (set in
 * Settings) so a student in Hawaii and one in Indiana see consistent
 * times for themselves. Because the timezone is a deliberate user
 * choice, we do NOT append a tz abbreviation to the output — the user
 * already knows what zone they\'re in.
 *
 * Storage: localStorage["coravu_user_timezone"]. If unset, we use the
 * browser\'s resolved Intl timezone.
 */

const TZ_KEY = "coravu_user_timezone";

/** Read the user\'s configured timezone or fall back to the browser default. */
export function getUserTimeZone(): string {
  if (typeof window !== "undefined") {
    try {
      const stored = window.localStorage?.getItem(TZ_KEY);
      if (stored) return stored;
    } catch {
      // fall through
    }
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

/** Persist a new timezone preference. Pass null to clear (browser default). */
export function setUserTimeZone(tz: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (tz === null) {
      window.localStorage?.removeItem(TZ_KEY);
    } else {
      window.localStorage?.setItem(TZ_KEY, tz);
    }
  } catch {
    // ignore — non-persistent fallback
  }
}

/** Browser-resolved system timezone, ignoring user preference. */
export function getSystemTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
  } catch {
    return "America/New_York";
  }
}

function fmtDate(d: Date, opts: Intl.DateTimeFormatOptions): string {
  return d.toLocaleDateString(undefined, { ...opts, timeZone: getUserTimeZone() });
}

function fmtTime(d: Date, opts: Intl.DateTimeFormatOptions): string {
  return d.toLocaleTimeString(undefined, { ...opts, timeZone: getUserTimeZone() });
}

/**
 * "Due May 8 at 8:35 PM" — long form for inline body text. No tz
 * abbreviation; the user has chosen their zone in Settings.
 */
export function formatDeadlineLong(iso: string | null | undefined): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const date = fmtDate(d, { month: "short", day: "numeric" });
  const time = fmtTime(d, { hour: "numeric", minute: "2-digit" });
  return `${date} at ${time}`;
}

/** "May 8 · 8:35 PM" — compact form for tight rows. */
export function formatDeadlineCompact(iso: string | null | undefined): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  const date = fmtDate(d, { month: "short", day: "numeric" });
  const time = fmtTime(d, { hour: "numeric", minute: "2-digit" });
  return `${date}  \u00B7  ${time}`;
}

/** Just the time portion. "8:35 PM". */
export function formatTimeWithZone(iso: string | null | undefined): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return fmtTime(d, { hour: "numeric", minute: "2-digit" });
}

/** "May 8" — short month + day, no time. */
export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return fmtDate(d, { month: "short", day: "numeric" });
}

/** "Mar 5, 2026" — date with year, no time. */
export function formatDateWithYear(iso: string | null | undefined): string {
  if (!iso) return "\u2014";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "\u2014";
  return fmtDate(d, { month: "short", day: "numeric", year: "numeric" });
}

/** Hours remaining until an ISO deadline, capped at zero. */
export function hoursUntil(iso: string | null | undefined): number {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return Number.POSITIVE_INFINITY;
  return Math.max(0, (t - Date.now()) / 3_600_000);
}

export function deadlineUrgency(iso: string | null | undefined): "soon" | "near" | "ok" | "past" {
  if (!iso) return "ok";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "ok";
  const ms = t - Date.now();
  if (ms <= 0) return "past";
  const hrs = ms / 3_600_000;
  if (hrs <= 24) return "soon";
  if (hrs <= 72) return "near";
  return "ok";
}

/**
 * Parse a free-text local date/time field into an ISO string. Returns a
 * friendly error instead of throwing when the text isn't a valid date, so a
 * typo can't crash a save with RangeError("Invalid time value"). An empty
 * field is valid and resolves to null (caller uses its own default).
 *
 * Single source of truth for the date inputs across the deliverable, case, and
 * promo editors — reuse this rather than calling `new Date(x).toISOString()`
 * directly on user input.
 */
export type ParsedDateField =
  | { ok: true; value: string | null }
  | { ok: false; msg: string };

export function parseDateTimeField(raw: string, label: string): ParsedDateField {
  const t = (raw ?? "").trim();
  if (!t) return { ok: true, value: null };
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) {
    return {
      ok: false,
      msg: `${label} isn't a valid date & time. Use the format YYYY-MM-DDTHH:MM - for example 2026-06-08T09:00.`,
    };
  }
  return { ok: true, value: d.toISOString() };
}

/**
 * Same validation as `parseDateTimeField`, but throws a friendly Error on
 * invalid input (never an unhandled RangeError). Use inside a save handler
 * already wrapped in try/catch that surfaces `e.message`.
 */
export function toIsoOrThrow(raw: string, label: string): string | null {
  const parsed = parseDateTimeField(raw, label);
  if (!parsed.ok) throw new Error(parsed.msg);
  return parsed.value;
}

/** Curated list of common timezones for the picker. */
export type TimezoneOption = { value: string; label: string };
export const COMMON_TIMEZONES: TimezoneOption[] = [
  { value: "America/New_York", label: "Eastern (New York)" },
  { value: "America/Chicago", label: "Central (Chicago)" },
  { value: "America/Denver", label: "Mountain (Denver)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
  { value: "America/Toronto", label: "Toronto" },
  { value: "America/Vancouver", label: "Vancouver" },
  { value: "America/Mexico_City", label: "Mexico City" },
  { value: "Europe/London", label: "London (UK)" },
  { value: "Europe/Dublin", label: "Dublin" },
  { value: "Europe/Paris", label: "Paris / Berlin / Madrid" },
  { value: "Europe/Athens", label: "Athens" },
  { value: "Africa/Lagos", label: "Lagos" },
  { value: "Africa/Johannesburg", label: "Johannesburg" },
  { value: "Asia/Dubai", label: "Dubai" },
  { value: "Asia/Karachi", label: "Karachi" },
  { value: "Asia/Kolkata", label: "Mumbai / Delhi" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Hong_Kong", label: "Hong Kong" },
  { value: "Asia/Shanghai", label: "Shanghai" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Seoul", label: "Seoul" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "Pacific/Auckland", label: "Auckland" },
];

// Backwards-compat alias for older imports.
export { getUserTimeZone as getResolvedTimeZone };
